
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Bookmark, Plus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/hooks/use-user";
import { useQuery } from "@tanstack/react-query";

type SaveToCollectionProps = {
  postId: string;
};

export function SaveToCollection({ postId }: SaveToCollectionProps) {
  const [selectedCollection, setSelectedCollection] = useState("");
  const [saving, setSaving] = useState(false);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const { user } = useUser();

  // Query to get all user's collections
  const { data: collections = [], refetch: refetchCollections } = useQuery({
    queryKey: ["userCollections", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("collections")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
        
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Query to check if post is saved in any collection
  const { data: savedInCollection, refetch: refetchSavedStatus } = useQuery({
    queryKey: ["savedCollection", user?.id, postId],
    queryFn: async () => {
      if (!user?.id || !postId) return null;
      
      const { data, error } = await supabase
        .from("collection_items")
        .select(`
          collection_id,
          collections (
            title,
            id
          )
        `)
        .eq("post_id", postId)
        .filter("collections.user_id", "eq", user.id)
        .maybeSingle();
        
      if (error && error.code !== "PGRST116") { // Not found error
        console.error("Error checking saved status:", error);
        return null;
      }
      
      return data;
    },
    enabled: !!user?.id && !!postId,
  });

  const handleSaveToCollection = async () => {
    if (!user?.id) {
      toast.error("Você precisa estar logado para salvar posts");
      return;
    }
    
    if (!selectedCollection) return;
    
    setSaving(true);
    try {
      if (selectedCollection === "new-folder") {
        setShowNewFolderDialog(true);
        setSaving(false);
        return;
      }

      // Check if post already exists in the collection
      const { data: existing } = await supabase
        .from("collection_items")
        .select("*")
        .eq("collection_id", selectedCollection)
        .eq("post_id", postId)
        .maybeSingle();

      if (existing) {
        toast.info("Este item já está salvo nesta coleção");
        setSaving(false);
        return;
      }

      // Save the post to the collection
      const { error } = await supabase
        .from("collection_items")
        .insert({
          collection_id: selectedCollection,
          post_id: postId
        });

      if (error) throw error;

      // Update collection cover if this is the first item
      await updateCollectionCover(selectedCollection, postId);

      toast.success("Salvo com sucesso!");
      setSelectedCollection("");
      refetchSavedStatus();
    } catch (error) {
      console.error("Error saving post:", error);
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const updateCollectionCover = async (collectionId: string, postId: string) => {
    // Get the image URL from the post
    const { data: post } = await supabase
      .from("posts")
      .select("image_url")
      .eq("id", postId)
      .single();
      
    if (!post?.image_url) {
      // Try getting from pins if not found in posts
      const { data: pin } = await supabase
        .from("pins")
        .select("image_url")
        .eq("id", postId)
        .single();
        
      if (!pin?.image_url) return;
      
      // Check if collection already has a cover
      const { data: collection } = await supabase
        .from("collections")
        .select("cover_image_url")
        .eq("id", collectionId)
        .single();
        
      // Only update cover if it doesn't already have one
      if (collection && !collection.cover_image_url) {
        await supabase
          .from("collections")
          .update({ cover_image_url: pin.image_url })
          .eq("id", collectionId);
      }
      
      return;
    }
      
    // Check if collection already has a cover
    const { data: collection } = await supabase
      .from("collections")
      .select("cover_image_url")
      .eq("id", collectionId)
      .single();
      
    // Only update cover if it doesn't already have one
    if (collection && !collection.cover_image_url) {
      await supabase
        .from("collections")
        .update({ cover_image_url: post.image_url })
        .eq("id", collectionId);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error("Digite um nome para a pasta");
      return;
    }

    setSaving(true);
    try {
      // Try getting image URL from posts first
      const { data: post } = await supabase
        .from("posts")
        .select("image_url")
        .eq("id", postId)
        .maybeSingle();

      let imageUrl = post?.image_url;
      
      // If not found, try pins
      if (!imageUrl) {
        const { data: pin } = await supabase
          .from("pins")
          .select("image_url")
          .eq("id", postId)
          .maybeSingle();
          
        imageUrl = pin?.image_url;
      }

      // Create new collection with cover image
      const { data: newCollection, error: createError } = await supabase
        .from("collections")
        .insert({
          title: newFolderName,
          user_id: user!.id,
          cover_image_url: imageUrl // Use the image as cover
        })
        .select()
        .single();

      if (createError) throw createError;

      // Save post to the new collection
      if (newCollection) {
        const { error: saveError } = await supabase
          .from("collection_items")
          .insert({
            collection_id: newCollection.id,
            post_id: postId
          });
          
        if (saveError) throw saveError;
      }

      toast.success("Nova pasta criada e post salvo com sucesso!");
      setShowNewFolderDialog(false);
      setNewFolderName("");
      refetchCollections();
      refetchSavedStatus();
    } catch (error) {
      console.error("Error creating folder:", error);
      toast.error("Erro ao criar nova pasta");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFromCollection = async () => {
    if (!savedInCollection?.collection_id) return;
    
    try {
      const { error } = await supabase
        .from("collection_items")
        .delete()
        .eq("post_id", postId)
        .eq("collection_id", savedInCollection.collection_id);
        
      if (error) throw error;
      
      toast.success("Removido da coleção");
      refetchSavedStatus();
    } catch (error) {
      console.error("Error removing from collection:", error);
      toast.error("Erro ao remover da coleção");
    }
  };

  if (!user) {
    return null;
  }

  if (savedInCollection) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 bg-primary/10 px-3 py-1.5 rounded-full">
          <Bookmark className="h-4 w-4" />
          <span className="text-sm">{savedInCollection.collections?.title}</span>
        </div>
        <Button size="sm" variant="ghost" onClick={handleRemoveFromCollection}>
          Remover
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Select value={selectedCollection} onValueChange={setSelectedCollection}>
          <SelectTrigger className="w-[160px] h-9">
            <SelectValue placeholder="Salvar em..." />
          </SelectTrigger>
          <SelectContent>
            {collections.map((collection: any) => (
              <SelectItem key={collection.id} value={collection.id}>
                {collection.title}
              </SelectItem>
            ))}
            <SelectItem value="new-folder" className="text-primary">
              <div className="flex items-center">
                <Plus className="h-4 w-4 mr-2" />
                Criar nova pasta
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
        <Button 
          size="sm" 
          onClick={handleSaveToCollection}
          disabled={!selectedCollection || saving}
        >
          Salvar
        </Button>
      </div>

      <Dialog open={showNewFolderDialog} onOpenChange={setShowNewFolderDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Criar nova pasta</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Nome da pasta"
              className="w-full"
            />
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowNewFolderDialog(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateFolder}
              disabled={saving || !newFolderName.trim()}
            >
              {saving ? "Criando..." : "Criar e salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
